import type { GenerationDetailJob } from "@/components/jobs/generation-job-detail"
import type { GenerationDetailLog, GenerationJobDetailData } from "@/lib/generation-job-detail"
import { readEpisodeIdFromUrl, workspaceUrlWithEpisode } from "@/lib/episode-timeline"
import { getCableConsumer, subscribeCableChannel } from "@/lib/cable-consumer"
import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  Activity,
  Boxes,
  ChevronDown,
  Download,
  FileJson,
  FileText,
  Film,
  Loader2,
  Menu,
  MessageSquare,
  Newspaper,
  Palette,
  PanelRight,
  RefreshCw,
  Search,
  Sparkles,
  SquareKanban,
  Users,
  Wallet,
  X,
} from "lucide-react"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps } from "react"
import { toast } from "sonner"
import { AppToaster } from "@/components/ui/app-toaster"
import { ConfirmHost } from "@/components/confirm-host"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { CommandPalette } from "@/components/command-palette"
import { WorkspaceProvidersForm } from "@/components/workspace-providers-form"
import { AGENT_COLUMN_HEADER_CLASS } from "@/components/workspace/agent-column-header"
import { Button, Kbd } from "@/components/ui/primitives"
import { CollapsedNavPeekProvider, SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import type { CompanyBalanceShare } from "@/components/billing/company-balance-nav"
import { ShellDrawer } from "@/components/shell/shell-drawer"
import { ShellMainPendingOverlay } from "@/components/shell/shell-main-pending-overlay"
import { AdminMenu } from "@/components/shell/admin-menu"
import { SidebarCompanyHeader } from "@/components/shell/sidebar-company-header"
import { SidebarToggleButton } from "@/components/shell/sidebar-toggle-button"
import { SidebarUserFooter } from "@/components/shell/sidebar-user-footer"
import { applyAccountPreferences } from "@/lib/preferences"
import type { AuthShare } from "@/types/auth"
import type { SpendLimitShare } from "@/types/billing"
import type { TaskNotificationsSummary } from "@/types/task-notifications"
import { useCompanyBalance } from "@/hooks/use-company-balance"
import { useSpendLimit } from "@/hooks/use-spend-limit"
import { useTaskNotifications } from "@/hooks/use-task-notifications"
import { postRestoreVersionSnapshot } from "@/lib/inertia-actions"
import { canUseOwnerProjectActions } from "@/lib/project-share-access"
import { usePermissions } from "@/hooks/use-permissions"
import { useShellNavUrl } from "@/hooks/use-shell-nav-url"
import { useSidebarToggleShortcut } from "@/hooks/use-sidebar-toggle-shortcut"
import { useTranslation } from "@/hooks/use-translation"
import i18n from "@/i18n"
import {
  acceptShellNavVisit,
  beginShellNav,
  noteShellNavPageSettled,
  noteShellNavVisitEnd,
  noteShellNavVisitStart,
} from "@/lib/shell-nav-pending"
import { closeShotForShellNav } from "@/lib/shell-nav-shot"
import {
  WORKSPACE_NAV_GROUPS,
  filterWorkspaceNavItemsByPermissions,
  readProjectIdFromWorkspaceUrl,
  type WorkspaceNavGroup,
  type WorkspaceNavItem,
} from "@/lib/workspace-nav"
import { filterNavGroupsBySections, sectionsVisibleToUser } from "@/lib/project-types"
import { applyProjectTypeI18n, clearProjectTypeI18n } from "@/lib/project-type-i18n"
import { APP_NAME, displayAppName } from "@/lib/brand"
import type { Theme } from "@/stores/ui-store"
import { desktopInertiaPrefetch, syncDesktopWindowAppearance } from "@/lib/desktop"
import { cn } from "@/lib/utils"
import { exportJobDownloadPath } from "@/lib/billing-exports"
import { noteIdFromUrl, urlWithoutNoteParam } from "@/lib/note-deep-link"
import { shellNavUrl } from "@/lib/task-board"
import { GenerationJobDetail } from "@/components/jobs/generation-job-detail"
import { formatElapsed, jobKindLabel, jobStatusLabel } from "@/components/jobs/job-elapsed-timer"
import { JobSubjectLabel } from "@/components/jobs/job-subject-label"
import { CAMERA_PRESET_DEMO_JOB_KIND } from "@/components/cameras/types"
import {
  JobRequestLogButton,
  WorkspaceDrawerActiveJobs,
  WorkspaceJobsFloater,
} from "@/components/jobs/workspace-jobs-floater"
import { WorkspaceJobsLifecycle } from "@/components/jobs/workspace-jobs-lifecycle"
import { AgentActivityDrawer } from "@/components/agent/agent-activity-drawer"
import {
  ToolbarJobsPanelActions,
  WORKSPACE_TOOLBAR_LEFT_CLASS,
  WORKSPACE_TOOLBAR_ROOT_CLASS,
} from "@/components/workspace/workspace-fullscreen-toolbar"
import { EMPTY_FULLSCREEN_TOOLBAR_SLOTS, FullscreenToolbarSlotContext } from "@/layouts/fullscreen-toolbar-slot"
import { AppMainModeProvider, useAppMainModeState } from "@/layouts/app-main-mode"
import { StudioPageChromeProvider, StudioSharedToolbar } from "@/layouts/studio-page-chrome"
import { WorkspaceShellTitleContext } from "@/layouts/workspace-shell-title"
import { ShotPanel } from "@/components/shot-panel/shot-panel"
import { SketchJobResultHost } from "@/components/sketch/sketch-job-result-host"
import { NotesDrawer } from "@/components/notes/notes-drawer"
import { type DrawerTab, useSidebarCollapsed, useUiStore } from "@/stores/ui-store"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import { useAgentRunsStore } from "@/stores/agent-runs-store"
import { useAgentActivityStore } from "@/stores/agent-activity-store"
import { useTaskNotificationsStore } from "@/stores/task-notifications-store"
import type { AgentActivityScope } from "@/lib/agent-activity-api"
import { agentActivityScopeKey } from "@/lib/agent-activity-api"
import { fetchAgentRuns, type AgentProjectRun } from "@/lib/agent-api"
import type {
  GenerationJobRow,
  GenerationModelsByContext,
  ProjectPermissions,
  ProjectSummary,
  WorkspaceVersionSidebarRow,
} from "@/types/workspace"
import { confirm } from "@/lib/confirm"

type RequestLogEntry = {
  id: number
  provider: string
  ai_model: string | null
  api_endpoint: string | null
  status: string
  request_payload: Record<string, unknown> | null
  response_payload: Record<string, unknown> | null
  error_message: string | null
  duration_ms: number | null
  created_at: string
  external_task_id: string | null
  loggable_id: number | null
}

type WorkspaceJobRow = GenerationJobRow & { request_logs?: RequestLogEntry[] }

const EPISODE_AWARE_NAV = new Set(["storyboard", "animatics", "assembly", "studio"])

function workspaceNavItemTo(item: WorkspaceNavItem, pid: number, episodeId?: number | null, assemblyView = false) {
  const base = `/projects/${pid}/workspace/${item.href}`
  const withEpisode = episodeId && EPISODE_AWARE_NAV.has(item.href) ? workspaceUrlWithEpisode(base, episodeId) : base
  if (item.href === "studio" && assemblyView) {
    return `${withEpisode}${withEpisode.includes("?") ? "&" : "?"}view=assembly`
  }
  return withEpisode
}

function workspaceNavItemActive(url: string, item: WorkspaceNavItem) {
  return url.includes(`/workspace/${item.href}`)
}

const ERROR_FRAGMENT_MARKER = "Фрагмент:"

function versionScopeLabel(scope: string) {
  const key = `layout:shell.versions.scope.${scope}`
  if (i18n.exists(key)) return i18n.t(key as never)
  return scope
}

function workspaceRestoreHint(scope: string) {
  if (scope === "episodes") return i18n.t("layout:shell.versions.restoreHint.episodes")
  if (scope === "scenes") return i18n.t("layout:shell.versions.restoreHint.scenes")
  return ""
}

function sidebarLinkClasses() {
  return {
    linkIdle:
      "text-[var(--ui-nav-idle-fg)] hover:bg-[var(--ui-nav-idle-hover-bg)] hover:text-[var(--ui-nav-idle-hover-fg)]",
    linkActive: "bg-[var(--ui-nav-active-bg)] text-[var(--ui-nav-active-fg)] light:shadow-[var(--shadow-clay-inset)]",
  }
}

function navItemClass(active: boolean) {
  const { linkIdle, linkActive } = sidebarLinkClasses()
  return cn("flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors", active ? linkActive : linkIdle)
}

function sidebarHrefString(href: ComponentProps<typeof Link>["href"]): string | null {
  if (typeof href === "string") return href
  if (href && typeof href === "object" && "url" in href && typeof href.url === "string") return href.url
  return null
}

function SidebarLink({ onClick, href, target, ...props }: ComponentProps<typeof Link>) {
  return (
    <Link
      {...props}
      href={href}
      target={target}
      prefetch={desktopInertiaPrefetch()}
      onClick={(event) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
        if (target === "_blank") return
        const to = sidebarHrefString(href)
        if (closeShotForShellNav(to)) {
          event.preventDefault()
          return
        }
        if (to) beginShellNav(to)
      }}
    />
  )
}

function SidebarIconLink({
  href,
  label,
  active,
  children,
}: {
  href: string
  label: string
  active: boolean
  children: React.ReactNode
}) {
  const { linkIdle, linkActive } = sidebarLinkClasses()
  const className = cn(
    "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
    active ? linkActive : linkIdle,
  )
  return (
    <SidebarTooltip label={label}>
      <SidebarLink href={href} className={className}>
        {children}
      </SidebarLink>
    </SidebarTooltip>
  )
}

type SidebarNavIcon = typeof Activity

function NavUnreadDot({ compact }: { compact?: boolean }) {
  const { t } = useTranslation("notifications")
  return (
    <>
      <span
        aria-hidden
        className={cn(
          "size-1.5 shrink-0 rounded-full bg-ui-accent",
          compact ? "pointer-events-none absolute top-1 right-1" : "ml-auto",
        )}
      />
      <span className="sr-only">{t("unread")}</span>
    </>
  )
}

/** Всегда вверху бокового меню */
function ProjectRootNavLinks({ url: serverUrl, compact }: { url: string; compact?: boolean }) {
  const url = useShellNavUrl(serverUrl)
  const { t } = useTranslation(["layout", "jobs"])
  const { can_create_projects: canCreateProjects } = usePermissions()
  const { linkIdle, linkActive } = sidebarLinkClasses()
  const hasUnread = useTaskNotificationsStore((s) => (s.summary?.unread ?? 0) > 0)
  const projectsActive = url === "/" || url.startsWith("/?")
  const feedActive = url === "/projects/feed" || url.startsWith("/projects/feed?")
  const tasksActive = url.startsWith("/company/tasks")

  if (compact) {
    return (
      <div className="flex flex-col items-center gap-1">
        <SidebarIconLink href="/" label={t("shell.root.projects")} active={projectsActive}>
          <Film className="h-4 w-4" />
        </SidebarIconLink>
        <SidebarIconLink href="/projects/feed" label={t("shell.root.feed")} active={feedActive}>
          <Newspaper className="h-4 w-4" />
        </SidebarIconLink>
        <div className="relative">
          <SidebarIconLink href="/company/tasks" label={t("shell.root.tasks")} active={tasksActive}>
            <SquareKanban className="h-4 w-4" />
          </SidebarIconLink>
          {hasUnread ? <NavUnreadDot compact /> : null}
        </div>
        {canCreateProjects ? (
          <SidebarIconLink
            href="/projects/new"
            label={t("shell.root.newProject")}
            active={url.startsWith("/projects/new")}
          >
            <Sparkles className="h-4 w-4" />
          </SidebarIconLink>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5">
      <SidebarLink
        href="/"
        className={cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
          projectsActive ? linkActive : linkIdle,
        )}
      >
        <Film className="h-4 w-4" />
        {t("shell.root.projects")}
      </SidebarLink>
      <SidebarLink
        href="/projects/feed"
        className={cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
          feedActive ? linkActive : linkIdle,
        )}
      >
        <Newspaper className="h-4 w-4" />
        {t("shell.root.feed")}
      </SidebarLink>
      <SidebarLink
        href="/company/tasks"
        className={cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
          tasksActive ? linkActive : linkIdle,
        )}
      >
        <SquareKanban className="h-4 w-4" />
        {t("shell.root.tasks")}
        {hasUnread ? <NavUnreadDot /> : null}
      </SidebarLink>
      {canCreateProjects ? (
        <SidebarLink
          href="/projects/new"
          className={cn("flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors", linkIdle)}
        >
          <Sparkles className="h-4 w-4" />
          {t("shell.root.newProject")}
        </SidebarLink>
      ) : null}
    </div>
  )
}

/** Мониторинг компании: генерации и диалоги с агентом. */
function MonitoringNavLinks({
  url: serverUrl,
  basePath,
  compact,
}: {
  url: string
  basePath: string
  compact?: boolean
}) {
  const url = useShellNavUrl(serverUrl)
  const { t } = useTranslation(["layout", "jobs"])
  const items = [
    { href: `${basePath}/generations`, label: t("shell.monitoring.generations"), icon: Activity },
    { href: `${basePath}/dialogs`, label: t("shell.monitoring.dialogs"), icon: MessageSquare },
  ]

  if (compact) {
    return (
      <div className="flex flex-col items-center gap-1">
        {items.map(({ href, label, icon: Icon }) => (
          <SidebarIconLink key={href} href={href} label={label} active={url.startsWith(href)}>
            <Icon className="h-4 w-4" />
          </SidebarIconLink>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-0.5">
      {items.map(({ href, label, icon: Icon }) => (
        <SidebarLink key={href} href={href} className={navItemClass(url.startsWith(href))}>
          <Icon className="h-4 w-4" />
          {label}
        </SidebarLink>
      ))}
    </div>
  )
}

type CompanyNavItem = { href: string; labelKey: string; icon: SidebarNavIcon }

const COMPANY_MEMBERS_NAV_ITEM: CompanyNavItem = {
  href: "/company/members",
  labelKey: "shell.company.members",
  icon: Users,
}

// Блок «Студия»: творческие инструменты компании (только управляющим).
// Камеры (/company/cameras) и «Обзор компании» (/company/overview) из сайдбара убраны.
const COMPANY_STUDIO_NAV_ITEMS: CompanyNavItem[] = [
  { href: "/company/canvas", labelKey: "shell.company.canvas", icon: Palette },
  { href: "/company/skills", labelKey: "shell.company.skills", icon: Sparkles },
  { href: "/company/system-prompts", labelKey: "shell.company.prompts", icon: FileText },
]

// Блок с названием компании: администрирование.
const COMPANY_ADMIN_NAV_ITEMS: CompanyNavItem[] = [
  { href: "/company/models", labelKey: "shell.company.models", icon: Boxes },
  { href: "/company/billing", labelKey: "shell.company.billing", icon: Wallet },
  COMPANY_MEMBERS_NAV_ITEM,
]

const MEMBER_COMPANY_NAV_ITEMS = [COMPANY_MEMBERS_NAV_ITEM]

function CompanyNavLinks({
  url: serverUrl,
  compact,
  items,
}: {
  url: string
  compact?: boolean
  items: CompanyNavItem[]
}) {
  const url = useShellNavUrl(serverUrl)
  const { t } = useTranslation(["layout", "jobs"])

  if (compact) {
    return (
      <div className="flex flex-col items-center gap-1">
        {items.map(({ href, labelKey, icon: Icon }) => (
          <SidebarIconLink
            key={href}
            href={href}
            label={t(labelKey, { defaultValue: labelKey })}
            active={url.startsWith(href)}
          >
            <Icon className="h-4 w-4" />
          </SidebarIconLink>
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5">
      {items.map(({ href, labelKey, icon: Icon }) => (
        <SidebarLink key={href} href={href} className={navItemClass(url.startsWith(href))}>
          <Icon className="h-4 w-4" />
          {t(labelKey, { defaultValue: labelKey })}
        </SidebarLink>
      ))}
    </div>
  )
}

/** Первая часть ошибки джобы — до технического хвоста (ERROR_FRAGMENT_MARKER). */
function jobErrorSummary(message: string): string {
  const fragmentAt = message.indexOf(ERROR_FRAGMENT_MARKER)
  return (fragmentAt !== -1 ? message.slice(0, fragmentAt) : message).trim()
}

/** Действия над строкой журнала в панели генерации: проверить OpenRouter, дополучить видео по task_id. */
function PayloadLogActions({
  log,
  projectId,
  canExpand,
}: {
  log: GenerationDetailLog
  projectId: number | undefined
  canExpand: boolean
}) {
  const { t } = useTranslation(["layout", "jobs"])
  const showCheckGeneration =
    canExpand && log.provider === "openrouter" && log.status === "failed" && projectId && log.loggable_id
  const showRecover =
    canExpand &&
    log.status === "failed" &&
    !!log.external_task_id &&
    !!projectId &&
    !!log.loggable_id &&
    (log.provider === "kling" || log.provider === "byteplus_seedance_video")

  const [genIdInput, setGenIdInput] = useState(log.external_task_id ?? "")
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState<{ ok: boolean; message: string } | null>(null)

  const [recovering, setRecovering] = useState(false)
  const [recoverResult, setRecoverResult] = useState<{ ok: boolean; message: string } | null>(null)

  const handleRecover = async () => {
    if (!projectId || !log.loggable_id || !log.external_task_id) return
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    setRecovering(true)
    setRecoverResult(null)
    try {
      const url = `/projects/${projectId}/workspace/shots/${log.loggable_id}/recover_generation`
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ log_id: log.id, external_task_id: log.external_task_id, authenticity_token: csrf }),
      })
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string; error?: string }
      if (data.ok) {
        setRecoverResult({ ok: true, message: data.message ?? t("shell.recover.fetched") })
        router.reload({ only: ["tree"] })
      } else {
        setRecoverResult({ ok: false, message: data.error ?? t("shell.recover.failed") })
      }
    } catch {
      setRecoverResult({ ok: false, message: t("shell.recover.networkUnavailable") })
    } finally {
      setRecovering(false)
    }
  }

  const handleCheckGeneration = async () => {
    if (!projectId || !log.loggable_id) return
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    const gid = genIdInput.trim()
    if (!gid) {
      setCheckResult({ ok: false, message: t("shell.openRouter.enterGenerationId") })
      return
    }
    setChecking(true)
    setCheckResult(null)
    try {
      const url = `/projects/${projectId}/workspace/shots/${log.loggable_id}/check_openrouter_generation`
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({ generation_id: gid, authenticity_token: csrf }),
      })
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string; error?: string }
      if (data.ok) {
        setCheckResult({ ok: true, message: data.message ?? t("shell.openRouter.imageSaved") })
        router.reload({ only: ["tree"] })
      } else {
        setCheckResult({ ok: false, message: data.error ?? t("shell.openRouter.imageNotFound") })
      }
    } catch {
      setCheckResult({ ok: false, message: t("shell.openRouter.networkUnavailable") })
    } finally {
      setChecking(false)
    }
  }

  if (!showCheckGeneration && !showRecover) return null

  return (
    <div className="flex flex-col gap-2">
      {showCheckGeneration ? (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-950/20 p-2.5">
          <p className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-ui-status-review">
            <RefreshCw className="h-3 w-3" />
            {t("shell.openRouter.checkGeneration")}
          </p>
          <p className="mb-2 text-[10px] leading-relaxed text-ui-text-muted">{t("shell.openRouter.checkHint")}</p>
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={genIdInput}
              onChange={(e) => setGenIdInput(e.target.value)}
              placeholder="generation_id"
              className="min-w-0 flex-1 rounded-md border border-ui-border bg-ui-bg-elevated px-2 py-1 font-mono text-[10px] text-ui-text-primary placeholder:text-ui-text-secondary focus:border-ui-accent focus:outline-none"
            />
            <button
              type="button"
              disabled={checking || !genIdInput.trim()}
              onClick={() => void handleCheckGeneration()}
              className="flex shrink-0 items-center gap-1 rounded-md bg-ui-accent/80 px-2 py-1 text-[10px] font-medium text-ui-on-accent hover:bg-ui-accent-hover/80 disabled:opacity-40"
            >
              {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {t("shell.openRouter.check")}
            </button>
          </div>
          {checkResult ? (
            <p
              className={cn("mt-1.5 text-[10px]", checkResult.ok ? "text-ui-status-approved" : "text-ui-status-failed")}
            >
              {checkResult.message}
            </p>
          ) : null}
        </div>
      ) : null}

      {showRecover ? (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-950/20 p-2.5">
          <p className="mb-1.5 flex items-center gap-1 text-[10px] font-medium text-ui-status-review">
            <RefreshCw className="h-3 w-3" />
            {t("shell.recover.checkAndFetch")}
          </p>
          <p className="mb-2 text-[10px] leading-relaxed text-ui-text-muted">{t("shell.recover.hint")}</p>
          <button
            type="button"
            disabled={recovering}
            onClick={() => void handleRecover()}
            className="flex w-full items-center justify-center gap-1 rounded-md bg-ui-accent/80 px-2 py-1.5 text-[10px] font-medium text-ui-on-accent hover:bg-ui-accent-hover/80 disabled:opacity-40"
          >
            {recovering ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {recovering ? t("shell.recover.checking") : t("shell.recover.checkAndFetch")}
          </button>
          {recoverResult ? (
            <p
              className={cn(
                "mt-1.5 text-[10px]",
                recoverResult.ok ? "text-ui-status-approved" : "text-ui-status-failed",
              )}
            >
              {recoverResult.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function WorkspaceSidebarNav({
  groups,
  collapsed,
  pid,
  episodeId,
  url: serverUrl,
  theme,
  hasTaskUnread,
  studioOpensAssembly,
}: {
  groups: readonly WorkspaceNavGroup[]
  collapsed: boolean
  pid: number
  episodeId: number | null
  url: string
  theme: Theme
  hasTaskUnread: boolean
  studioOpensAssembly: boolean
}) {
  const url = useShellNavUrl(serverUrl)
  const { t } = useTranslation(["layout", "jobs"])

  return (
    <div className="flex flex-col">
      {collapsed ? (
        <div className="flex w-full flex-col items-center">
          {groups.map((group, gi) => {
            const isProduction = group.id === "production"
            const prevIsProduction = gi > 0 && groups[gi - 1]?.id === "production"
            const showDivider = gi > 0 && !isProduction && !prevIsProduction
            return (
              <div
                key={group.id}
                className={cn(
                  "flex w-full flex-col items-center",
                  gi > 0 && "mt-2 pt-2",
                  showDivider && "border-t border-ui-border",
                )}
              >
                <div
                  className={cn(
                    "flex w-full flex-col items-center gap-1",
                    isProduction &&
                      "rounded-lg border px-0.5 py-1 dark:border-ui-accent/40 dark:bg-ui-accent/15 light:border-ui-border-strong light:bg-clay-1 light:shadow-[var(--shadow-clay-inset),var(--shadow-sm)]",
                  )}
                >
                  {group.items.map((item) => {
                    const { href, labelKey, icon: Icon } = item
                    const to = workspaceNavItemTo(item, pid, episodeId, studioOpensAssembly)
                    const active = workspaceNavItemActive(url, item)
                    return (
                      <SidebarTooltip key={href} label={t(labelKey, { defaultValue: href })}>
                        <SidebarLink
                          href={to}
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
                            active
                              ? theme === "dark"
                                ? "bg-ui-accent/25 text-ui-accent-text"
                                : "bg-ui-accent-soft text-ui-accent-text"
                              : theme === "dark"
                                ? "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                                : "text-ui-text-secondary hover:bg-ui-surface-sunken hover:text-ui-text-primary",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {href === "tasks" && hasTaskUnread ? <NavUnreadDot compact /> : null}
                        </SidebarLink>
                      </SidebarTooltip>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        groups.map((group, gi) => {
          const isProduction = group.id === "production"
          const prevIsProduction = gi > 0 && groups[gi - 1]?.id === "production"
          const showDivider = gi > 0 && !isProduction && !prevIsProduction
          const renderGroupLinks = (emphasized: boolean) => (
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const { href, labelKey, icon: Icon } = item
                const to = workspaceNavItemTo(item, pid, episodeId, studioOpensAssembly)
                const active = workspaceNavItemActive(url, item)
                return (
                  <SidebarLink
                    key={href}
                    href={to}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                      active
                        ? emphasized
                          ? "dark:bg-ui-accent/25 dark:dark:text-violet-100 dark:shadow-sm dark:dark:shadow-violet-950/40 light:bg-[var(--ui-nav-active-bg)] light:text-ui-accent-text light:shadow-[var(--shadow-sm)]"
                          : "bg-[var(--ui-nav-active-bg)] text-[var(--ui-nav-active-fg)] light:shadow-[var(--shadow-clay-inset)]"
                        : emphasized
                          ? "text-ui-text-primary hover:bg-[var(--ui-nav-idle-hover-bg)] hover:text-[var(--ui-nav-idle-hover-fg)]"
                          : "text-[var(--ui-nav-idle-fg)] hover:bg-[var(--ui-nav-idle-hover-bg)] hover:text-[var(--ui-nav-idle-hover-fg)]",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", emphasized ? "opacity-90" : "opacity-80")} />
                    <span className={cn("truncate", emphasized && "font-medium")}>
                      {t(labelKey, { defaultValue: href })}
                    </span>
                    {href === "tasks" && hasTaskUnread ? <NavUnreadDot /> : null}
                  </SidebarLink>
                )
              })}
            </div>
          )

          return (
            <div
              key={group.id}
              className={cn(gi === 0 ? "mt-0" : "mt-4 pt-3", showDivider && "border-t border-ui-border")}
            >
              {isProduction ? (
                <div className="rounded-lg border border-ui-border bg-[var(--ui-nav-active-bg)] p-2.5">
                  <div className="mb-2 px-0.5 text-[10px] font-bold tracking-wider text-ui-accent-text uppercase">
                    {t(group.labelKey, { defaultValue: group.id })}
                  </div>
                  {renderGroupLinks(true)}
                </div>
              ) : (
                <>
                  <div
                    className={cn(
                      "mb-1.5 px-0.5 text-[10px] font-semibold tracking-wider uppercase",
                      "text-ui-text-muted",
                    )}
                  >
                    {t(group.labelKey, { defaultValue: group.id })}
                  </div>
                  {renderGroupLinks(false)}
                </>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <AppMainModeProvider>
      <AppShellInner>{children}</AppShellInner>
    </AppMainModeProvider>
  )
}

function AppShellInner({ children }: { children: React.ReactNode }) {
  const page = usePage<{
    flash?: { notice?: string; alert?: string }
    project?: ProjectSummary
    auth?: AuthShare
    company_balance?: CompanyBalanceShare | null
    spend_limit?: SpendLimitShare | null
    task_notifications?: TaskNotificationsSummary | null
    project_permissions?: ProjectPermissions
    workspace_versions?: WorkspaceVersionSidebarRow[]
    generation_models_by_context?: GenerationModelsByContext
    active_episode_id?: number | null
  }>()
  const url = shellNavUrl(page.url)
  const inWorkspace = url.includes("/workspace/")
  const pidFromUrl = useMemo(() => readProjectIdFromWorkspaceUrl(url), [url])
  // Props can briefly go missing on jobs partial reloads — keep sidebar/nav on URL fallback.
  const pid = page.props.project?.id ?? pidFromUrl
  const storeJobsProjectId = useWorkspaceJobsStore((s) => s.projectId)
  const workspaceEpisodeNavId = useMemo(() => {
    const fromUrl = readEpisodeIdFromUrl(url)
    if (fromUrl) return fromUrl
    if (page.props.active_episode_id) return page.props.active_episode_id
    return page.props.project?.active_episode_id ?? null
  }, [page.props.active_episode_id, page.props.project?.active_episode_id, url])
  const studioSystemProjectId = (page.props as { studio_system_project_id?: number }).studio_system_project_id
  const companyJobsProjectId = (page.props as { company_jobs_project_id?: number }).company_jobs_project_id
  const inStudioAdmin = url.includes("/studio/")
  const inCompanyAdmin = url.startsWith("/company/")
  // Платформа делит панель задач с проектом студии: там живут её фоновые выгрузки.
  const inPlatformAdmin = url.startsWith("/platform/")
  // Prefer props/URL pid, then store — avoid panel "open a project" when props.project briefly missing.
  const workspaceJobsPid =
    pid ?? (storeJobsProjectId != null && storeJobsProjectId > 0 ? storeJobsProjectId : undefined)
  const jobsContextProjectId = inWorkspace
    ? workspaceJobsPid
    : inStudioAdmin || inPlatformAdmin
      ? studioSystemProjectId
      : inCompanyAdmin
        ? companyJobsProjectId
        : undefined
  const jobsPanelProjectId = jobsContextProjectId
  const companyId = page.props.auth?.company?.id ?? null
  const companyBalance = useCompanyBalance(page.props.company_balance ?? null, companyId)
  const spendLimit = useSpendLimit(page.props.spend_limit ?? null, companyId)
  useTaskNotifications(page.props.task_notifications ?? null, companyId)
  const hasTaskUnread = useTaskNotificationsStore((s) => (s.summary?.unread ?? 0) > 0)
  const agentsActivityScope = useMemo((): AgentActivityScope | null => {
    if (inCompanyAdmin) {
      return companyId ? { kind: "company", id: companyId } : null
    }
    return jobsPanelProjectId ? { kind: "project", id: jobsPanelProjectId } : null
  }, [inCompanyAdmin, companyId, jobsPanelProjectId])
  const agentsScopeKey = agentsActivityScope ? agentActivityScopeKey(agentsActivityScope) : null
  const agentsReadyUnreadCount = useAgentActivityStore((s) =>
    agentsScopeKey && s.scopeKey === agentsScopeKey ? s.readyUnreadCount : 0,
  )
  const canViewRequestLogDetails =
    page.props.project_permissions?.can_view_request_log_details === true || page.props.auth?.user?.super_admin === true
  const companyPermissions = usePermissions()
  const isSuperAdmin = page.props.auth?.user?.super_admin === true
  const billingExportAccess = useMemo(
    () => ({ company: companyPermissions.can_manage_members === true, platform: isSuperAdmin }),
    [companyPermissions.can_manage_members, isSuperAdmin],
  )
  const showPlatformNav = page.props.auth?.platform_nav === true
  const canRestoreVersions = canUseOwnerProjectActions(page.props.project_permissions?.shared, isSuperAdmin)
  const showGenerationsNav = isSuperAdmin || companyPermissions.can_view_monitoring
  const navSections = sectionsVisibleToUser(page.props.project?.type_config?.allowed_sections, isSuperAdmin)
  const navGroups = filterNavGroupsBySections(
    filterWorkspaceNavItemsByPermissions(WORKSPACE_NAV_GROUPS, page.props.project_permissions, showGenerationsNav),
    navSections,
  )
  const { t, locale } = useTranslation(["layout", "jobs", "workspace"])
  const accountLocale = page.props.auth?.user?.locale ?? null
  const accountTheme = page.props.auth?.user?.theme ?? null
  const {
    theme,
    commandOpen,
    toggleCommand,
    jobsOpen,
    setJobsOpen,
    drawerOpen,
    setDrawerOpen,
    drawerTab,
    setDrawerTab,
    mobileNavOpen,
    setMobileNavOpen,
    toggleSidebar,
  } = useUiStore()
  const [linkedNoteId, setLinkedNoteId] = useState<number | null>(null)
  const handledNoteUrl = useRef<string | null>(null)
  const consumeLinkedNote = useCallback(() => setLinkedNoteId(null), [])

  useEffect(() => {
    const id = noteIdFromUrl(page.url)
    if (id == null) {
      handledNoteUrl.current = null
      return
    }
    if (handledNoteUrl.current === page.url) return
    handledNoteUrl.current = page.url
    setDrawerOpen(true)
    setDrawerTab("notes")
    setLinkedNoteId(id)
    const next = urlWithoutNoteParam(page.url)
    if (next !== page.url) window.history.replaceState(window.history.state, "", next)
  }, [page.url, setDrawerOpen, setDrawerTab])

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light")
    document.documentElement.classList.toggle("dark", theme === "dark")
    syncDesktopWindowAppearance(theme)
  }, [theme])

  useEffect(() => {
    applyAccountPreferences(accountLocale, accountTheme)
  }, [accountLocale, accountTheme])

  useLayoutEffect(() => {
    if (inWorkspace && page.props.project?.project_type) {
      applyProjectTypeI18n(page.props.project.project_type)
    } else {
      clearProjectTypeI18n()
    }
  }, [inWorkspace, page.props.project?.project_type])

  const skipFlashForUrlCountsRef = useRef(new Map<string, number>())
  /** False when visit.only preserves flash props (partial reload) — do not re-toast stale notice. */
  const flashFreshOnVisitRef = useRef(true)

  // Зависимость — весь `page`: иначе при том же тексте flash (дважды «Проверить n8n») строка
  // notice не меняется и useEffect не срабатывает.
  useEffect(() => {
    const pendingSkips = skipFlashForUrlCountsRef.current.get(page.url) ?? 0
    if (pendingSkips > 0) {
      skipFlashForUrlCountsRef.current.set(page.url, pendingSkips - 1)
      return
    }
    if (!flashFreshOnVisitRef.current) return
    const notice = page.props.flash?.notice
    const alert = page.props.flash?.alert
    if (notice) toast.success(notice)
    if (alert) toast.error(alert)
  }, [page])

  const inProjectContext = inWorkspace && !!pid
  const isStudioPage = url.includes("/workspace/studio")
  const isCanvasPage = url.includes("/workspace/canvas") || url.startsWith("/company/canvas")
  const isFlowPage = url.includes("/workspace/flow")
  const isAnimaticsPage = url.includes("/workspace/animatics")
  const isStoryboardPage = url.includes("/workspace/storyboard")
  const isAssemblyPage = url.includes("/workspace/assembly")
  const isOverviewPage = url.includes("/workspace/overview")
  const isGenerationsWorkspacePage = url.includes("/workspace/generations")
  const isSeasonsWorkspacePage = url.includes("/workspace/seasons")
  const isScriptWorkspacePage = url.includes("/workspace/script")
  const isCharactersWorkspacePage = url.includes("/workspace/characters")
  const isLocationsWorkspacePage = url.includes("/workspace/locations")
  const isScenes3dWorkspacePage = url.includes("/workspace/scenes_3d")
  const isPropsWorkspacePage = url.includes("/workspace/props")
  const isCameraPresetsWorkspacePage = url.includes("/workspace/camera_presets")
  const isPresetsWorkspacePage = url.includes("/workspace/presets")
  const isGenerationSkillsWorkspacePage = url.includes("/workspace/skills")
  const isLibraryWorkspacePage = url.includes("/workspace/library")
  const isSettingsWorkspacePage = url.includes("/workspace/settings")
  const isTeamWorkspacePage = url.includes("/workspace/team")
  const isBillingWorkspacePage = url.includes("/workspace/billing")
  const isTasksWorkspacePage = url.includes("/workspace/tasks")
  const isFeedWorkspacePage = url.includes("/workspace/feed")
  const isPresentationEditorPage = /\/studio\/presentations\/\d+/.test(url)
  const isPublicPresentationPage = url.startsWith("/p/")
  const projectsPathOnly = url.split("?")[0]
  // Layout shells (SidePeekLayout / DenseListPage / MasterDetail flush) register
  // main scroll mode — no URL allowlists for DataTable scrollport.
  const mainMode = useAppMainModeState()
  const isViewportLockedPage = mainMode !== "default"
  const isFullScreenPage =
    isStudioPage ||
    isCanvasPage ||
    isFlowPage ||
    isAnimaticsPage ||
    isStoryboardPage ||
    isAssemblyPage ||
    isOverviewPage ||
    isGenerationsWorkspacePage ||
    isSeasonsWorkspacePage ||
    isScriptWorkspacePage ||
    isCharactersWorkspacePage ||
    isLocationsWorkspacePage ||
    isScenes3dWorkspacePage ||
    isPropsWorkspacePage ||
    isCameraPresetsWorkspacePage ||
    isPresetsWorkspacePage ||
    isGenerationSkillsWorkspacePage ||
    isLibraryWorkspacePage ||
    isSettingsWorkspacePage ||
    isTeamWorkspacePage ||
    isBillingWorkspacePage ||
    isTasksWorkspacePage ||
    isFeedWorkspacePage ||
    isPresentationEditorPage ||
    isPublicPresentationPage
  const isProjectsListOrWizard =
    projectsPathOnly === "/" ||
    projectsPathOnly === "/projects" ||
    projectsPathOnly === "/projects/" ||
    // Лента студии — будущая главная (docs/studio_feed.md): та же шапка, что у списка проектов.
    projectsPathOnly === "/projects/feed" ||
    projectsPathOnly === "/projects/new" ||
    projectsPathOnly.startsWith("/projects/new/")
  /** Non-project AppShell pages: shared toolbar chrome (Search was in sticky header). */
  const isSharedToolbarPage =
    !isFullScreenPage &&
    (url.startsWith("/company/") ||
      url.startsWith("/studio/") ||
      url.startsWith("/platform/") ||
      url.startsWith("/account/") ||
      isProjectsListOrWizard)
  const usesToolbarChrome = isFullScreenPage || isSharedToolbarPage
  const jobsHeaderActive = jobsOpen || (drawerOpen && drawerTab === "jobs_history")
  const collapsed = useSidebarCollapsed(inProjectContext)
  const toggleCurrentSidebar = useCallback(() => toggleSidebar(inProjectContext), [toggleSidebar, inProjectContext])
  useSidebarToggleShortcut(toggleCurrentSidebar)
  // Fullscreen panels that leave the rail visible (ShotPanel) take their left inset from this.
  useEffect(() => {
    document.documentElement.toggleAttribute("data-shell-sidebar-expanded", !collapsed)
    return () => document.documentElement.removeAttribute("data-shell-sidebar-expanded")
  }, [collapsed])
  const [collapsedNavHovered, setCollapsedNavHovered] = useState(false)
  const navRef = useRef<HTMLElement>(null)
  const desktopBodyRef = useRef<HTMLDivElement>(null)
  const [navScrolled, setNavScrolled] = useState(false)

  useEffect(() => {
    if (!collapsed) setCollapsedNavHovered(false)
  }, [collapsed])

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    nav.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: "nearest" })
    setNavScrolled(nav.scrollTop > 0)
  }, [url, collapsed, inProjectContext])

  const drawerTabs = useMemo((): DrawerTab[] => ["agents", "settings", "jobs_history", "notes"], [])

  const drawerTabLabel = useCallback(
    (tab: DrawerTab) => {
      if (tab === "settings") return t("shell.drawer.settings")
      if (tab === "jobs_history") return t("shell.drawer.jobs")
      if (tab === "agents") return t("shell.drawer.agents")
      if (tab === "notes") return t("shell.drawer.notes")
      return t("shell.drawer.versions")
    },
    [t],
  )

  const fullscreenToolbarSlotsRef = useRef({
    left: null as HTMLDivElement | null,
    actions: null as HTMLDivElement | null,
  })
  const [fullscreenToolbarSlots, setFullscreenToolbarSlots] = useState(EMPTY_FULLSCREEN_TOOLBAR_SLOTS)
  const flushToolbarSlotsPendingRef = useRef(false)
  // Left + create-action hosts (Jobs/Panel live in AppShell). Ignore ref(null) mid-remount;
  // clear only when leaving toolbar-chrome pages (effect below). Coalesce both refs into one setState.
  const flushFullscreenToolbarSlots = useCallback(() => {
    if (flushToolbarSlotsPendingRef.current) return
    flushToolbarSlotsPendingRef.current = true
    queueMicrotask(() => {
      flushToolbarSlotsPendingRef.current = false
      const next = {
        left: fullscreenToolbarSlotsRef.current.left,
        actions: fullscreenToolbarSlotsRef.current.actions,
      }
      setFullscreenToolbarSlots((prev) => (prev.left === next.left && prev.actions === next.actions ? prev : next))
    })
  }, [])
  const assignFullscreenToolbarSlot = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      fullscreenToolbarSlotsRef.current.left = node
      flushFullscreenToolbarSlots()
    },
    [flushFullscreenToolbarSlots],
  )
  const assignFullscreenToolbarActionsSlot = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      fullscreenToolbarSlotsRef.current.actions = node
      flushFullscreenToolbarSlots()
    },
    [flushFullscreenToolbarSlots],
  )

  useEffect(() => {
    if (usesToolbarChrome) return
    fullscreenToolbarSlotsRef.current = { left: null, actions: null }
    setFullscreenToolbarSlots(EMPTY_FULLSCREEN_TOOLBAR_SLOTS)
  }, [usesToolbarChrome])

  const [shellTitle, setShellTitle] = useState<string | null>(null)
  const shellTitleContext = useMemo(() => ({ setShellTitle }), [])

  const setWorkspaceJobs = useWorkspaceJobsStore((s) => s.setJobs)
  const setWorkspaceJobsFromSnapshot = useWorkspaceJobsStore((s) => s.setJobsFromSnapshot)
  // Boolean selector: progress ticks must not re-render shell chrome / toolbar host.
  const hasActiveJobs = useWorkspaceJobsStore((s) =>
    s.jobs.some((j) => j.status === "queued" || j.status === "processing"),
  )

  const workspaceVersions: WorkspaceVersionSidebarRow[] = page.props.workspace_versions ?? []

  const [payloadModal, setPayloadModal] = useState<{ jobId: number; jobLabel: string; shotIds: number[] } | null>(null)
  const [jobRequestLogsCache, setJobRequestLogsCache] = useState<Record<number, GenerationJobDetailData>>({})
  const [jobRequestLogsErrorJobId, setJobRequestLogsErrorJobId] = useState<number | null>(null)
  const payloadModalJobId = payloadModal?.jobId
  const payloadModalJobActive = useWorkspaceJobsStore((s) =>
    payloadModalJobId != null
      ? s.jobs.some((j) => j.id === payloadModalJobId && (j.status === "queued" || j.status === "processing"))
      : false,
  )

  useEffect(() => {
    setJobRequestLogsCache({})
    setJobRequestLogsErrorJobId(null)
    if (!jobsPanelProjectId) {
      setPayloadModal(null)
    }
  }, [jobsPanelProjectId])

  useEffect(() => {
    if (!payloadModal || !jobsPanelProjectId) return
    const jobId = payloadModal.jobId
    let cancelled = false
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""

    const fetchLogs = (opts?: { silent?: boolean }) => {
      fetch(`/projects/${jobsPanelProjectId}/workspace/generation_jobs/${jobId}/request_logs`, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf,
        },
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(String(res.status))
          return res.json() as Promise<GenerationJobDetailData>
        })
        .then((data) => {
          if (!cancelled) {
            setJobRequestLogsErrorJobId((prev) => (prev === jobId ? null : prev))
            setJobRequestLogsCache((prev) => ({
              ...prev,
              [jobId]: { ...data, logs: Array.isArray(data.logs) ? data.logs : [] },
            }))
          }
        })
        .catch(() => {
          if (!cancelled && !opts?.silent) {
            setJobRequestLogsErrorJobId(jobId)
            setJobRequestLogsCache((prev) => ({ ...prev, [jobId]: prev[jobId] ?? { logs: [] } }))
          }
        })
    }

    fetchLogs()

    if (!payloadModalJobActive) {
      return () => {
        cancelled = true
      }
    }

    const timer = window.setInterval(() => fetchLogs({ silent: true }), 4000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [payloadModal, jobsPanelProjectId, payloadModalJobActive])

  const payloadModalData = !payloadModal ? null : (jobRequestLogsCache[payloadModal.jobId] ?? null)
  // Строка джобы для панели: живая из стора, иначе из истории, иначе — по подписи из кнопки.
  const payloadModalStoreJob = useWorkspaceJobsStore((s) =>
    payloadModalJobId != null ? (s.jobs.find((j) => j.id === payloadModalJobId) ?? null) : null,
  )
  const awaitingPayloadLogs =
    Boolean(payloadModal) &&
    Boolean(jobsPanelProjectId) &&
    jobRequestLogsErrorJobId !== payloadModal?.jobId &&
    !Object.prototype.hasOwnProperty.call(jobRequestLogsCache, payloadModal?.jobId ?? -1)
  const payloadLogsFetchError =
    payloadModal && jobRequestLogsErrorJobId === payloadModal.jobId ? t("jobs:requestLogsLoadFailed") : null

  // --- Jobs history for sidebar tab ---
  type JobsHistoryPage = {
    items: WorkspaceJobRow[]
    page: number
    hasMore: boolean
    loading: boolean
    error: string | null
  }
  const [jobsHistory, setJobsHistory] = useState<JobsHistoryPage>({
    items: [],
    page: 0,
    hasMore: true,
    loading: false,
    error: null,
  })
  const payloadModalJob: GenerationDetailJob = payloadModalStoreJob ??
    jobsHistory.items.find((j) => j.id === payloadModalJobId) ?? {
      id: payloadModalJobId ?? 0,
      job_kind: "",
      status: payloadModalData?.telemetry?.phase ? "processing" : "completed",
      subject_label: payloadModal?.jobLabel ?? null,
      shot_ids: payloadModal?.shotIds ?? [],
    }
  const [agentActivityRefreshNonce, setAgentActivityRefreshNonce] = useState(0)
  const jobsHistoryLoadedForPid = useRef<number | undefined>(undefined)
  const jobsSnapshotLoadedForPid = useRef<number | undefined>(undefined)
  const prevJobsPanelPidRef = useRef<number | undefined>(undefined)

  const loadJobsHistory = useCallback((projectId: number, pageNum: number, reset = false) => {
    setJobsHistory((prev) => ({ ...prev, loading: true, error: null }))
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
    fetch(`/projects/${projectId}/workspace/generation_jobs/history?page=${pageNum}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": csrf },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.json() as Promise<{ jobs: WorkspaceJobRow[]; has_more: boolean; page: number }>
      })
      .then((data) => {
        setJobsHistory((prev) => {
          if (!reset) {
            const existingIds = new Set(prev.items.map((item) => item.id))
            const appended = data.jobs.filter((j) => !existingIds.has(j.id))
            return {
              items: [...prev.items, ...appended],
              page: data.page,
              hasMore: data.has_more,
              loading: false,
              error: null,
            }
          }
          // Keep session-prepended terminal rows that API page might not include yet.
          const apiIds = new Set(data.jobs.map((j) => j.id))
          const keptLocal = prev.items.filter((j) => !apiIds.has(j.id))
          return {
            items: [...keptLocal, ...data.jobs],
            page: data.page,
            hasMore: data.has_more,
            loading: false,
            error: null,
          }
        })
      })
      .catch(() => {
        setJobsHistory((prev) => ({ ...prev, loading: false, error: i18n.t("jobs:historyLoadFailed") }))
      })
  }, [])

  useEffect(() => {
    if (
      drawerOpen &&
      drawerTab === "jobs_history" &&
      jobsPanelProjectId &&
      jobsHistoryLoadedForPid.current !== jobsPanelProjectId
    ) {
      jobsHistoryLoadedForPid.current = jobsPanelProjectId
      loadJobsHistory(jobsPanelProjectId, 1, true)
    }
  }, [drawerOpen, drawerTab, jobsPanelProjectId, loadJobsHistory])

  useEffect(() => {
    if (!jobsPanelProjectId) return
    const panelVisible = jobsOpen || (drawerOpen && drawerTab === "jobs_history")
    if (!panelVisible) return
    if (jobsSnapshotLoadedForPid.current === jobsPanelProjectId) return
    const stored = useWorkspaceJobsStore.getState()
    if (stored.projectId === jobsPanelProjectId && stored.jobs.length > 0) {
      jobsSnapshotLoadedForPid.current = jobsPanelProjectId
      return
    }
    jobsSnapshotLoadedForPid.current = jobsPanelProjectId
    router.reload({ only: ["jobs"], preserveUrl: true, except: ["flash"], preserveErrors: true })
  }, [jobsOpen, drawerOpen, drawerTab, jobsPanelProjectId])

  // Wipe history only when switching to a different project — not on brief undefined flicker.
  useEffect(() => {
    if (jobsPanelProjectId == null) return
    const prev = prevJobsPanelPidRef.current
    prevJobsPanelPidRef.current = jobsPanelProjectId
    if (prev != null && prev !== jobsPanelProjectId) {
      jobsHistoryLoadedForPid.current = undefined
      jobsSnapshotLoadedForPid.current = undefined
      setJobsHistory({ items: [], page: 0, hasMore: true, loading: false, error: null })
    }
  }, [jobsPanelProjectId])

  const onNewlyTerminalJobs = useCallback((newlyTerminal: GenerationJobRow[]) => {
    setJobsHistory((prev) => {
      const existingIds = new Set(prev.items.map((item) => item.id))
      const toAdd = newlyTerminal.filter((j) => !existingIds.has(j.id))
      if (toAdd.length === 0) return prev
      return { ...prev, items: [...toAdd, ...prev.items] }
    })
  }, [])

  const visitOnlyRef = useRef<string[]>([])
  const jobsContextProjectIdRef = useRef<number | undefined>(undefined)
  jobsContextProjectIdRef.current = jobsContextProjectId

  useLayoutEffect(() => {
    if (!jobsContextProjectId) return
    const jobs = (page.props as { jobs?: WorkspaceJobRow[] }).jobs
    if (!Array.isArray(jobs)) return
    setWorkspaceJobs(jobsContextProjectId, jobs)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync jobs only on navigation, not partial reloads
  }, [url, jobsContextProjectId])

  useEffect(() => {
    const offStart = router.on("start", (event) => {
      const only = event.detail.visit.only ?? []
      const except = event.detail.visit.except ?? []
      visitOnlyRef.current = only
      // Fresh flash only on full visits or when flash is explicitly in only.
      flashFreshOnVisitRef.current = except.includes("flash") ? false : only.length === 0 || only.includes("flash")
      if (except.includes("flash")) {
        const visitUrl =
          typeof event.detail.visit.url === "string"
            ? event.detail.visit.url
            : event.detail.visit.url.pathname + event.detail.visit.url.search
        const current = skipFlashForUrlCountsRef.current.get(visitUrl) ?? 0
        skipFlashForUrlCountsRef.current.set(visitUrl, current + 1)
      }
    })
    const offSuccess = router.on("success", (event) => {
      const only = visitOnlyRef.current
      if (only.length > 0 && !only.includes("jobs")) return
      const j = (event.detail.page.props as { jobs?: WorkspaceJobRow[] }).jobs
      const pid = jobsContextProjectIdRef.current
      if (pid && Array.isArray(j)) useWorkspaceJobsStore.getState().setJobsFromSnapshot(pid, j)
    })
    return () => {
      offStart()
      offSuccess()
    }
  }, [])

  const pageUrlRef = useRef(page.url)
  pageUrlRef.current = page.url

  useEffect(() => {
    const offBefore = router.on("before", (event) => {
      acceptShellNavVisit(event.detail.visit, () => event.defaultPrevented, pageUrlRef.current)
    })
    const offNavStart = router.on("start", (event) => {
      noteShellNavVisitStart(event.detail.visit)
    })
    const offNavSuccess = router.on("success", (event) => {
      noteShellNavPageSettled(event.detail.page.url)
    })
    const offFinish = router.on("finish", (event) => {
      noteShellNavVisitEnd(event.detail.visit)
    })
    return () => {
      offBefore()
      offNavStart()
      offNavSuccess()
      offFinish()
    }
  }, [])

  const workspaceCableFallbackRef = useRef<number | null>(null)
  const workspaceCableFallbackActiveRef = useRef(false)

  useEffect(() => {
    if (!jobsContextProjectId) {
      if (workspaceCableFallbackRef.current !== null) {
        window.clearInterval(workspaceCableFallbackRef.current)
        workspaceCableFallbackRef.current = null
      }
      workspaceCableFallbackActiveRef.current = false
      return
    }

    const cableProjectId = jobsContextProjectId

    const reloadJobsOnly = () => {
      if (document.hidden) return
      router.reload({
        only: ["jobs"],
        preserveErrors: true,
        except: ["flash"],
        preserveUrl: true,
      })
    }

    // Плашки делегирования держат спиннер по статусу прогона целевого ассистента.
    const reloadAgentRuns = () => {
      if (document.hidden) return
      void fetchAgentRuns(cableProjectId)
        .then((runs) => useAgentRunsStore.getState().setRuns(cableProjectId, runs))
        .catch(() => {})
    }

    const clearFallback = () => {
      if (workspaceCableFallbackRef.current !== null) {
        window.clearInterval(workspaceCableFallbackRef.current)
        workspaceCableFallbackRef.current = null
      }
      workspaceCableFallbackActiveRef.current = false
    }

    const onVisibilityChange = () => {
      if (!document.hidden && workspaceCableFallbackActiveRef.current) {
        reloadJobsOnly()
        reloadAgentRuns()
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange)

    const consumer = getCableConsumer()
    const unsubscribe = subscribeCableChannel(
      consumer,
      { channel: "Projects::WorkspaceChannel", project_id: cableProjectId },
      {
        connected() {
          clearFallback()
          reloadAgentRuns()
          const hasActiveJobs = useWorkspaceJobsStore
            .getState()
            .jobs.some((j) => j.status === "queued" || j.status === "processing")
          if (hasActiveJobs) {
            reloadJobsOnly()
          }
        },
        disconnected() {
          clearFallback()
          workspaceCableFallbackActiveRef.current = true
          workspaceCableFallbackRef.current = window.setInterval(() => {
            reloadJobsOnly()
            reloadAgentRuns()
          }, 15_000)
        },
        received(data: unknown) {
          if (!data || typeof data !== "object" || !("type" in data)) return
          const type = (data as { type: string }).type
          if (type === "generation_jobs" && "jobs" in data && Array.isArray((data as { jobs: unknown }).jobs)) {
            setWorkspaceJobsFromSnapshot(cableProjectId, (data as { jobs: WorkspaceJobRow[] }).jobs)
            setAgentActivityRefreshNonce((n) => n + 1)
            return
          }
          if (type === "agent_run" && "agent_run" in data) {
            const run = (data as { agent_run: AgentProjectRun }).agent_run
            if (run?.conversation_id) useAgentRunsStore.getState().mergeRun(cableProjectId, run)
            setAgentActivityRefreshNonce((n) => n + 1)
          }
        },
      },
    )

    return () => {
      clearFallback()
      document.removeEventListener("visibilitychange", onVisibilityChange)
      unsubscribe()
    }
  }, [jobsContextProjectId, setWorkspaceJobsFromSnapshot])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [setMobileNavOpen, url])

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const onChange = () => {
      if (mq.matches) setMobileNavOpen(false)
    }
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [setMobileNavOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [mobileNavOpen, setMobileNavOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileNavOpen])

  return (
    <>
      <WorkspaceJobsLifecycle
        jobsContextProjectId={jobsContextProjectId}
        jobsPanelProjectId={jobsPanelProjectId}
        onNewlyTerminal={onNewlyTerminalJobs}
      />
      <WorkspaceShellTitleContext.Provider value={shellTitleContext}>
        <StudioPageChromeProvider>
          <FullscreenToolbarSlotContext.Provider value={fullscreenToolbarSlots}>
            <TooltipPrimitive.Provider delayDuration={150} skipDelayDuration={100}>
              <div
                data-desktop-root
                className={cn(
                  "text-ui-text-primary transition-colors",
                  isFullScreenPage || isSharedToolbarPage || isViewportLockedPage
                    ? "h-screen overflow-hidden"
                    : "min-h-screen",
                  "bg-ui-bg",
                )}
              >
                <Head title={APP_NAME} />
                <AppToaster theme={theme} />
                <ConfirmHost />

                <CommandPalette
                  open={commandOpen}
                  onOpenChange={(v) => useUiStore.setState({ commandOpen: v })}
                  projectId={pid}
                  superAdmin={showPlatformNav}
                  allowedSections={navSections}
                  canCreateProjects={companyPermissions.can_create_projects}
                />

                <div
                  data-desktop-row
                  className={cn(
                    "relative flex",
                    isFullScreenPage || isSharedToolbarPage || isViewportLockedPage ? "h-screen" : "min-h-screen",
                  )}
                >
                  {mobileNavOpen ? (
                    <div
                      role="presentation"
                      className="fixed inset-0 z-30 bg-[var(--ui-overlay-light)] md:hidden"
                      onClick={() => setMobileNavOpen(false)}
                    />
                  ) : null}

                  {usesToolbarChrome && (
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className={cn(
                        "fixed top-1 left-1.5 z-50 h-9 w-9 shrink-0 backdrop-blur-md md:hidden",
                        "bg-ui-bg-elevated/70 text-ui-text-primary dark:hover:bg-zinc-800/80 light:hover:bg-ui-surface-sunken",
                      )}
                      aria-expanded={mobileNavOpen}
                      aria-controls="app-sidebar"
                      aria-label={mobileNavOpen ? t("shell.sidebar.closeMenu") : t("shell.sidebar.openMenu")}
                      onClick={() => setMobileNavOpen(!mobileNavOpen)}
                    >
                      {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </Button>
                  )}

                  <aside
                    id="app-sidebar"
                    className={cn(
                      "flex shrink-0 flex-col border-r pb-3 transition-[width] duration-200",
                      collapsed ? "w-14 px-1.5" : "w-[var(--shell-sidebar-width)]",
                      "fixed bottom-0 left-0 z-40 transition-transform duration-200 ease-out",
                      usesToolbarChrome ? "top-0 pt-14 md:pt-3" : "top-14 pt-3",
                      "-translate-x-full",
                      mobileNavOpen && "translate-x-0",
                      // md+: shared grid chrome puts sidebar under the bar (see ui-theme.css)
                      "md:sticky md:top-auto md:z-auto md:h-full md:translate-x-0",
                      "border-ui-border dark:bg-ui-bg-elevated light:bg-ui-bg-elevated",
                    )}
                  >
                    <div className={cn("mb-4", !collapsed && "px-3")}>
                      {page.props.auth ? (
                        <SidebarCompanyHeader
                          auth={page.props.auth}
                          balance={companyBalance}
                          spendLimit={spendLimit}
                          collapsed={collapsed}
                          railHovered={collapsedNavHovered}
                          onRailHoverChange={(hovered) => setCollapsedNavHovered(collapsed && hovered)}
                          onAction={() => setMobileNavOpen(false)}
                        />
                      ) : null}
                    </div>

                    <nav
                      ref={navRef}
                      className={cn(
                        "flex flex-1 flex-col overflow-x-hidden overflow-y-auto",
                        collapsed ? "it2tv-scrollbar-hidden" : "it2tv-scrollbar",
                      )}
                      onScroll={(e) => setNavScrolled(e.currentTarget.scrollTop > 0)}
                      onMouseEnter={() => {
                        if (collapsed) setCollapsedNavHovered(true)
                      }}
                      onMouseLeave={() => setCollapsedNavHovered(false)}
                    >
                      <div
                        aria-hidden
                        className={cn(
                          "pointer-events-none sticky top-0 z-10 -mb-3 h-3 shrink-0 bg-gradient-to-b from-ui-bg-elevated to-transparent transition-opacity",
                          navScrolled ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className={cn(!collapsed && "px-3")}>
                        <CollapsedNavPeekProvider enabled={collapsed} railHovered={collapsedNavHovered}>
                          {!inProjectContext ? <ProjectRootNavLinks url={url} compact={collapsed} /> : null}

                          {inProjectContext && pid ? (
                            <WorkspaceSidebarNav
                              groups={navGroups}
                              collapsed={collapsed}
                              pid={pid}
                              episodeId={workspaceEpisodeNavId}
                              url={url}
                              theme={theme}
                              hasTaskUnread={hasTaskUnread}
                              studioOpensAssembly={!isSuperAdmin}
                            />
                          ) : null}

                          {!inProjectContext && page.props.auth?.company && companyPermissions.can_manage_members && (
                            <div
                              className={cn(
                                "mt-4 border-t pt-3",
                                "border-ui-border",
                                collapsed && "flex flex-col items-center",
                              )}
                            >
                              {!collapsed ? (
                                <div className="mb-1.5 truncate px-0.5 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase">
                                  {t("shell.sidebar.studio")}
                                </div>
                              ) : null}
                              <CompanyNavLinks url={url} compact={collapsed} items={COMPANY_STUDIO_NAV_ITEMS} />
                            </div>
                          )}

                          {!inProjectContext && page.props.auth?.company && companyPermissions.can_manage_members && (
                            <div
                              className={cn(
                                "mt-4 border-t pt-3",
                                "border-ui-border",
                                collapsed && "flex flex-col items-center",
                              )}
                            >
                              {!collapsed ? (
                                <div
                                  className="mb-1.5 truncate px-0.5 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase"
                                  title={displayAppName(page.props.auth.company)}
                                >
                                  {displayAppName(page.props.auth.company)}
                                </div>
                              ) : null}
                              <CompanyNavLinks url={url} compact={collapsed} items={COMPANY_ADMIN_NAV_ITEMS} />
                              {!showPlatformNav ? (
                                <div className={cn(collapsed ? "mt-1" : "mt-3")}>
                                  {!collapsed ? (
                                    <div className="mb-1.5 px-0.5 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase">
                                      {t("shell.monitoring.section")}
                                    </div>
                                  ) : null}
                                  <MonitoringNavLinks url={url} basePath="/company/monitoring" compact={collapsed} />
                                </div>
                              ) : null}
                            </div>
                          )}

                          {!inProjectContext && page.props.auth?.company && !companyPermissions.can_manage_members && (
                            <div
                              className={cn(
                                "mt-4 border-t pt-3",
                                "border-ui-border",
                                collapsed && "flex flex-col items-center",
                              )}
                            >
                              {!collapsed ? (
                                <div
                                  className="mb-1.5 truncate px-0.5 text-[10px] font-semibold tracking-wider text-ui-text-muted uppercase"
                                  title={displayAppName(page.props.auth.company)}
                                >
                                  {displayAppName(page.props.auth.company)}
                                </div>
                              ) : null}
                              <CompanyNavLinks url={url} compact={collapsed} items={MEMBER_COMPANY_NAV_ITEMS} />
                            </div>
                          )}
                        </CollapsedNavPeekProvider>
                      </div>
                    </nav>

                    {showPlatformNav ? (
                      <div className={cn("mt-2 shrink-0", collapsed ? "flex justify-center" : "px-3")}>
                        <AdminMenu url={url} collapsed={collapsed} onAction={() => setMobileNavOpen(false)} />
                      </div>
                    ) : null}

                    <div className={cn("mt-2 border-t border-ui-border pt-2", !collapsed && "px-3")}>
                      <SidebarUserFooter
                        user={page.props.auth?.user ?? null}
                        collapsed={collapsed}
                        active={url.startsWith("/account/")}
                        railHovered={collapsedNavHovered}
                        onRailHoverChange={(hovered) => setCollapsedNavHovered(collapsed && hovered)}
                        onAction={() => setMobileNavOpen(false)}
                      />
                    </div>
                  </aside>

                  <div
                    data-desktop-main
                    className={cn(
                      "flex min-w-0 flex-1 flex-col",
                      (usesToolbarChrome || isViewportLockedPage || isCanvasPage || isFlowPage) &&
                        "min-h-0 overflow-hidden",
                    )}
                  >
                    <header
                      data-desktop-window-bar
                      className={cn(
                        "sticky top-0 z-50 flex h-14 items-center justify-between gap-3 border-b px-6 backdrop-blur-md",
                        "border-ui-border dark:bg-black/30 light:bg-ui-bg-elevated/80",
                        usesToolbarChrome && "hidden",
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          className="shrink-0 text-ui-text-secondary md:hidden"
                          aria-expanded={mobileNavOpen}
                          aria-controls="app-sidebar"
                          aria-label={mobileNavOpen ? t("shell.sidebar.closeMenu") : t("shell.sidebar.openMenu")}
                          onClick={() => setMobileNavOpen(!mobileNavOpen)}
                        >
                          {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                        <SidebarToggleButton collapsed={collapsed} onToggle={toggleCurrentSidebar} />
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          className="text-ui-text-secondary"
                          onClick={toggleCommand}
                        >
                          <Search className="mr-1 h-4 w-4" />
                          {t("shell.header.search")}
                          <Kbd className="ml-2 px-1.5 text-[10px]">⌘K</Kbd>
                        </Button>
                        {page.props.project && (
                          <div className="truncate text-sm font-medium text-ui-text-primary">
                            {shellTitle ?? page.props.project.name}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={jobsHeaderActive ? "secondary" : "ghost"}
                          size="sm"
                          type="button"
                          className={jobsHeaderActive ? undefined : "border border-transparent"}
                          onClick={() => {
                            if (hasActiveJobs) {
                              setJobsOpen(!jobsOpen)
                            } else {
                              setDrawerOpen(true)
                              setDrawerTab("agents")
                            }
                          }}
                        >
                          {hasActiveJobs ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin text-ui-link" />
                          ) : (
                            <Activity className="mr-1 h-4 w-4" />
                          )}
                          {t("shell.header.jobs")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={() => {
                            if (!drawerOpen) {
                              setDrawerTab("agents")
                              setDrawerOpen(true)
                            } else {
                              setDrawerOpen(false)
                            }
                          }}
                          aria-label={t("shell.header.contextAria")}
                        >
                          <PanelRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </header>

                    {usesToolbarChrome && (
                      <div data-desktop-window-bar className={WORKSPACE_TOOLBAR_ROOT_CLASS}>
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <SidebarToggleButton collapsed={collapsed} onToggle={toggleCurrentSidebar} />
                          <div ref={assignFullscreenToolbarSlot} className={WORKSPACE_TOOLBAR_LEFT_CLASS}>
                            {isSharedToolbarPage ? <StudioSharedToolbar /> : null}
                          </div>
                        </div>
                        <ToolbarJobsPanelActions
                          actionsSlotRef={assignFullscreenToolbarActionsSlot}
                          activityScope={agentsActivityScope}
                          refreshNonce={agentActivityRefreshNonce}
                        />
                      </div>
                    )}

                    <div
                      ref={desktopBodyRef}
                      data-desktop-body
                      className={cn(
                        "flex flex-1",
                        (usesToolbarChrome || isViewportLockedPage || isCanvasPage || isFlowPage) && "min-h-0",
                      )}
                    >
                      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                        <main
                          // Скроллится сам main, а не window — без scroll-region Inertia не сбрасывает
                          // его позицию при переходах (preserveScroll на действиях таблиц продолжает работать).
                          {...{ "scroll-region": "" }}
                          className={cn(
                            "isolate min-w-0 flex-1",
                            isFullScreenPage && (isCanvasPage || isFlowPage)
                              ? "flex min-h-0 flex-col overflow-hidden p-0"
                              : isFullScreenPage
                                ? "flex min-h-0 flex-col overflow-x-hidden overflow-y-visible p-0"
                                : mainMode === "flush"
                                  ? "flex min-h-0 flex-col overflow-hidden p-0"
                                  : mainMode === "scrollport"
                                    ? // @container: контент шире скроллпорта, поэтому липким элементам
                                      // нужна ширина видимой области (cqw), а не ширина таблицы.
                                      // overflow-auto: оба оси на main — иначе nested overflow-x ломает sticky th.
                                      "@container/scrollport min-h-0 overflow-auto p-0"
                                    : isSharedToolbarPage
                                      ? "it2tv-scrollbar min-h-0 overflow-x-hidden overflow-y-auto p-4"
                                      : "overflow-y-auto p-6 lg:p-10",
                          )}
                        >
                          {children}
                        </main>
                        <ShellMainPendingOverlay />
                      </div>

                      {drawerOpen ? (
                        <div
                          role="presentation"
                          data-shell-drawer-backdrop
                          className="fixed inset-0 bg-[var(--ui-overlay-light)] lg:hidden"
                          onClick={() => setDrawerOpen(false)}
                        />
                      ) : null}

                      <ShellDrawer
                        open={drawerOpen}
                        containerRef={desktopBodyRef}
                        usesToolbarChrome={usesToolbarChrome}
                      >
                        <div className={cn(AGENT_COLUMN_HEADER_CLASS, "gap-1 overflow-visible")}>
                          <div className="flex min-h-0 min-w-0 flex-1 items-center gap-1 overflow-visible">
                            {drawerTabs.map((tab) => {
                              const label = drawerTabLabel(tab)
                              const active = drawerTab === tab
                              const agentsBadge =
                                tab === "agents" && agentsReadyUnreadCount > 0
                                  ? agentsReadyUnreadCount > 99
                                    ? "99+"
                                    : String(agentsReadyUnreadCount)
                                  : null
                              return (
                                <button
                                  key={tab}
                                  type="button"
                                  className={cn(
                                    "relative flex shrink-0 items-center px-2.5 text-xs font-medium whitespace-nowrap transition-colors",
                                    tab === "agents" && "pr-3",
                                    active
                                      ? theme === "dark"
                                        ? "text-ui-accent-text"
                                        : "text-ui-link"
                                      : theme === "dark"
                                        ? "text-ui-text-muted hover:text-ui-text-secondary"
                                        : "text-ui-text-muted hover:text-ui-text-secondary",
                                  )}
                                  onClick={() => setDrawerTab(tab)}
                                >
                                  {label}
                                  {agentsBadge ? (
                                    <span className="absolute -top-1.5 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-ui-text-primary tabular-nums dark:bg-zinc-700 light:bg-clay-3">
                                      {agentsBadge}
                                    </span>
                                  ) : null}
                                </button>
                              )
                            })}
                          </div>
                        </div>

                        <div className="it2tv-scrollbar relative flex-1 overflow-y-auto overscroll-contain p-5">
                          {drawerTab === "settings" && (
                            <>
                              {inWorkspace && page.props.project ? (
                                <WorkspaceProvidersForm
                                  project={page.props.project}
                                  generationModelsByContext={page.props.generation_models_by_context}
                                />
                              ) : (
                                <p className="text-xs text-ui-text-muted">{t("shell.drawer.openProjectForModels")}</p>
                              )}
                            </>
                          )}

                          {drawerTab === "jobs_history" && (
                            <>
                              {!jobsPanelProjectId ? (
                                <p className="text-xs text-ui-text-muted">{t("shell.drawer.openProjectForJobs")}</p>
                              ) : (
                                // -mx-5 снимает p-5 тела панели: отступы как у «Агентов» (px-1.5 + строка p-1.5).
                                <div className="-mx-5 space-y-5 px-1.5">
                                  <WorkspaceDrawerActiveJobs
                                    projectId={jobsPanelProjectId}
                                    theme={theme}
                                    billingExportAccess={billingExportAccess}
                                    onOpenPayload={setPayloadModal}
                                  />

                                  <div>
                                    <p className="mb-2 px-1.5 text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
                                      {t("jobs:recent")}
                                    </p>
                                    {jobsHistory.loading && jobsHistory.items.length === 0 ? (
                                      <div className="flex justify-center py-10">
                                        <Loader2 className="h-6 w-6 animate-spin text-ui-link" />
                                      </div>
                                    ) : jobsHistory.error && jobsHistory.items.length === 0 ? (
                                      <p className="text-xs text-ui-status-failed">{jobsHistory.error}</p>
                                    ) : jobsHistory.items.length === 0 && !hasActiveJobs ? (
                                      <p className="text-xs text-ui-text-muted">{t("jobs:noJobs")}</p>
                                    ) : jobsHistory.items.length === 0 ? null : (
                                      <div className="flex flex-col">
                                        {jobsHistory.items.map((j) => {
                                          const statusLabel = jobStatusLabel(j.status)
                                          const statusTone =
                                            j.status === "failed"
                                              ? "text-ui-status-failed"
                                              : j.status === "completed"
                                                ? "text-ui-status-approved"
                                                : "text-ui-text-muted"
                                          const jobLabel = j.subject_label || jobKindLabel(j.job_kind)
                                          const exportDownloadPath = exportJobDownloadPath(j, billingExportAccess)
                                          const finishedAt = j.updated_at || j.created_at
                                          const durationMs =
                                            j.started_at && j.updated_at
                                              ? new Date(j.updated_at).getTime() - new Date(j.started_at).getTime()
                                              : 0
                                          const metaLine = [
                                            jobKindLabel(j.job_kind),
                                            finishedAt
                                              ? new Date(finishedAt).toLocaleString(
                                                  locale === "en" ? "en-US" : "ru-RU",
                                                  {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                  },
                                                )
                                              : null,
                                            durationMs > 0 ? formatElapsed(durationMs) : null,
                                          ]
                                            .filter(Boolean)
                                            .join(" · ")
                                          const showError =
                                            canViewRequestLogDetails &&
                                            j.status === "failed" &&
                                            j.error_message &&
                                            j.job_kind !== CAMERA_PRESET_DEMO_JOB_KIND
                                          return (
                                            <div
                                              key={j.id}
                                              className="flex gap-2.5 rounded-lg p-1.5 text-xs hover:bg-ui-surface-raised"
                                            >
                                              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                <div className="min-w-0 overflow-hidden">
                                                  <JobSubjectLabel
                                                    job={j}
                                                    projectId={jobsPanelProjectId}
                                                    tone="plain"
                                                    weight="normal"
                                                  />
                                                </div>
                                                <span className="truncate text-[11px] text-ui-text-muted">
                                                  {metaLine}
                                                </span>
                                                {showError ? (
                                                  <span
                                                    className="truncate text-[11px] text-ui-status-failed"
                                                    title={j.error_message ?? undefined}
                                                  >
                                                    {jobErrorSummary(j.error_message ?? "")}
                                                  </span>
                                                ) : null}
                                              </div>
                                              <div className="flex shrink-0 items-start gap-1.5">
                                                <span className={cn("text-[11px]", statusTone)}>{statusLabel}</span>
                                                {exportDownloadPath ? (
                                                  <a
                                                    href={exportDownloadPath}
                                                    title={t("jobs:downloadExport")}
                                                    aria-label={t("jobs:downloadExport")}
                                                    className="text-ui-text-muted transition-colors hover:text-ui-link"
                                                  >
                                                    <Download className="h-3.5 w-3.5" />
                                                  </a>
                                                ) : null}
                                                {j.billing_export_id ? null : (
                                                  <JobRequestLogButton
                                                    jobId={j.id}
                                                    jobLabel={jobLabel}
                                                    shotIds={j.shot_ids ?? []}
                                                    onOpen={setPayloadModal}
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          )
                                        })}
                                        {jobsHistory.hasMore && jobsPanelProjectId && (
                                          <div className="flex justify-center pt-2">
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="secondary"
                                              disabled={jobsHistory.loading}
                                              onClick={() => loadJobsHistory(jobsPanelProjectId, jobsHistory.page + 1)}
                                            >
                                              {jobsHistory.loading ? (
                                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                              ) : (
                                                <ChevronDown className="mr-1 h-3 w-3" />
                                              )}
                                              {t("jobs:loadMore")}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <Link
                                      href={`/projects/${jobsPanelProjectId}/workspace/generations`}
                                      className="mt-2 inline-block px-1.5 text-xs text-ui-link hover:underline"
                                    >
                                      {t("jobs:allProjectGenerations")}
                                    </Link>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {drawerTab === "agents" && (
                            <AgentActivityDrawer
                              scope={agentsActivityScope}
                              theme={theme}
                              refreshNonce={agentActivityRefreshNonce}
                            />
                          )}

                          {drawerTab === "notes" && (
                            <NotesDrawer noteId={linkedNoteId} onNoteLinkConsumed={consumeLinkedNote} />
                          )}

                          {drawerTab === "versions" && (
                            <>
                              <p className="text-xs leading-relaxed text-ui-text-muted">{t("shell.versions.intro")}</p>
                              {inWorkspace && pid ? (
                                <Link
                                  href={`/projects/${pid}/workspace/versions`}
                                  className={cn(
                                    "mt-3 block text-xs font-medium underline-offset-2 hover:underline",
                                    "text-ui-link",
                                  )}
                                >
                                  {t("shell.versions.fullHistoryLink")}
                                </Link>
                              ) : null}
                              <div className="mt-4 space-y-2">
                                {!inWorkspace || !pid ? (
                                  <p className="text-xs text-ui-text-muted">{t("shell.versions.openProject")}</p>
                                ) : workspaceVersions.length === 0 ? (
                                  <p className="text-xs text-ui-text-muted">{t("shell.versions.noSnapshots")}</p>
                                ) : (
                                  workspaceVersions.map((v) => {
                                    const restorable = Boolean(v.restorable && canRestoreVersions)
                                    const scopeLabel = versionScopeLabel(v.scope)
                                    return (
                                      <div
                                        key={v.id}
                                        className={cn(
                                          "rounded-lg border p-3 text-xs",
                                          theme === "dark"
                                            ? "border-ui-border bg-ui-bg-elevated/[0.04]"
                                            : "border-ui-border bg-ui-surface-sunken",
                                          restorable && "cursor-pointer",
                                        )}
                                        title={restorable ? t("shell.versions.doubleClickRestore") : undefined}
                                        onDoubleClick={async () => {
                                          if (!restorable || !pid) return
                                          const hint = workspaceRestoreHint(v.scope)
                                          if (
                                            !(await confirm({
                                              message: t("shell.versions.restoreConfirm", {
                                                hint,
                                                label: v.label ?? String(v.id),
                                              }),
                                              tone: "danger",
                                            }))
                                          ) {
                                            return
                                          }
                                          postRestoreVersionSnapshot(pid, v.id)
                                        }}
                                      >
                                        <div className="flex items-start justify-between gap-2">
                                          <span className={cn("font-medium", "text-ui-text-primary")}>
                                            {scopeLabel}
                                            {v.label ? ` · ${v.label}` : ""}
                                          </span>
                                          <span className="shrink-0 text-[10px] text-ui-text-muted">
                                            {new Date(v.created_at).toLocaleString("ru-RU", {
                                              day: "2-digit",
                                              month: "2-digit",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </span>
                                        </div>
                                        {v.system_note ? (
                                          <p className="mt-1.5 line-clamp-2 text-[11px] text-ui-text-muted">
                                            {v.system_note}
                                          </p>
                                        ) : null}
                                        {restorable ? (
                                          <p className="mt-2 text-[10px] text-ui-text-muted">
                                            {t("shell.versions.doubleClickRestore")}
                                          </p>
                                        ) : (
                                          <p
                                            className={cn(
                                              "mt-2 text-[10px]",
                                              theme === "dark" ? "text-ui-text-muted" : "text-ui-text-secondary",
                                            )}
                                          >
                                            {v.restorable_reason ?? t("shell.versions.restoreUnavailable")}
                                          </p>
                                        )}
                                      </div>
                                    )
                                  })
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </ShellDrawer>

                      <WorkspaceJobsFloater
                        projectId={jobsPanelProjectId}
                        theme={theme}
                        billingExportAccess={billingExportAccess}
                        onOpenPayload={setPayloadModal}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {payloadModal && (
                <div
                  className="fixed inset-0 z-[110] flex items-center justify-center bg-[var(--ui-overlay-heavy)] backdrop-blur-sm"
                  onClick={() => setPayloadModal(null)}
                >
                  <div
                    className="max-h-[85vh] w-[720px] max-w-[92vw] overflow-hidden rounded-lg border border-ui-border shadow-2xl dark:bg-zinc-950 light:bg-ui-bg-elevated"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between border-b border-ui-border px-5 py-3">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-ui-text-primary">
                        <FileJson className="h-4 w-4 text-ui-link" />
                        {payloadModal.jobLabel}
                      </h3>
                      <button
                        type="button"
                        className="rounded-md p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                        onClick={() => setPayloadModal(null)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="it2tv-scrollbar max-h-[calc(85vh-52px)] overflow-auto px-5 py-4">
                      <GenerationJobDetail
                        job={payloadModalJob}
                        projectId={jobsPanelProjectId ?? 0}
                        data={payloadModalData}
                        loading={awaitingPayloadLogs}
                        error={payloadLogsFetchError}
                        canViewLogs={canViewRequestLogDetails}
                        hideHeader
                        logExtras={(log) => (
                          <PayloadLogActions
                            log={log}
                            projectId={jobsPanelProjectId}
                            canExpand={canViewRequestLogDetails}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
              <ShotPanel />
              <SketchJobResultHost />
            </TooltipPrimitive.Provider>
          </FullscreenToolbarSlotContext.Provider>
        </StudioPageChromeProvider>
      </WorkspaceShellTitleContext.Provider>
    </>
  )
}
